import * as THREE from 'three';
import React, { forwardRef } from 'react';
import { ThreeElements } from '@react-three/fiber';
import { Anchor } from './types';

/**
 * This shader is from the THREE's SpriteMaterial.
 * We need to turn the backing plane into a Sprite (make it always face the camera)
 * if "transform" is false.
 * https://github.com/mrdoob/three.js/blob/4ec743df6500ae6fc93f3a28ed02a9912e01f443/src/renderers/shaders/ShaderLib/sprite.glsl.js
 */
const vertexShader = `
uniform vec2 center;
#include <common>

void main() {

  // This is somewhat arbitrary, but it seems to work well
  // Need to figure out how to derive this dynamically if it even matters
  float sizeX = 0.058;
  float sizeY = 0.062;

  vec4 mvPosition = modelViewMatrix[ 3 ];

	vec2 scale = vec2( length( modelMatrix[ 0 ].xyz ), length( modelMatrix[ 1 ].xyz ) );
 

  bool isPerspective = isPerspectiveMatrix( projectionMatrix );
  if ( isPerspective ) scale *= - mvPosition.z;

  vec2 alignedPosition = ( position.xy - ( center - vec2( 0.5 ) ) ) * scale;
  alignedPosition.x *= sizeX;
  alignedPosition.y *= sizeY;

  mvPosition.xy += alignedPosition;

  gl_Position = projectionMatrix * mvPosition;
}
`;

const fragmentShader = `
void main() {
  gl_FragColor = vec4(0.0, 0.0, 0.0, 0.0);
}
`;

export type OcclusionMeshProps = Omit<ThreeElements['mesh'], 'geometry' | 'material'> & {
  /**
   * If true, the mesh will always face the camera.
   * This is useful for occlusion meshes that need to be
   * always visible from the camera's perspective.
   */
  sprite?: boolean;
  /**
   * Custom geometry for the occlusion mesh.
   * Defaults to a plane geometry if not provided.
   */
  geometry?: React.ReactNode;
  /**
   * Custom material for the occlusion mesh.
   * Defaults to a shader material if not provided.
   */
  material?: React.ReactNode;

  /**
   * Anchor point for the occlusion mesh.
   * Determines where the mesh is positioned relative to its parent.
   * Defaults to 'top-left'.
   */
  anchor?: Anchor;
};

const getCenterFromAnchor = (anchor: Anchor): [number, number] => {
  switch (anchor) {
    case 'top-left':
      return [0, 1];
    case 'top-right':
      return [1, 1];
    case 'bottom-left':
      return [0, 0];
    case 'bottom-right':
      return [1, 0];
    case 'center':
      return [0.5, 0.5];
  }
};

/**
 * OcclusionMesh is a React component that creates a mesh
 * used for occlusion in 3D scenes. It can be configured
 * with custom geometry and material.
 */
export const OcclusionMesh = forwardRef<THREE.Mesh, OcclusionMeshProps>(
  (
    {
      sprite,
      geometry,
      anchor = 'top-left',
      material = (
        <shaderMaterial
          side={THREE.DoubleSide}
          vertexShader={sprite ? vertexShader : undefined}
          fragmentShader={fragmentShader}
          uniforms={{
            center: { value: new THREE.Vector2(...getCenterFromAnchor(anchor)) },
          }}
        />
      ),
      ...restProps
    },
    forwardedRef,
  ) => {
    return (
      <mesh ref={forwardedRef} {...restProps}>
        {geometry || <planeGeometry />}
        {material}
      </mesh>
    );
  },
);
